import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Bot, GraduationCap, CheckCircle2, ChevronRight, Lock, Trophy } from "lucide-react";
import { T, PrimaryBtn, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";

/**
 * Free Assessment — predicted band result + plan upsell.
 * In preview mode we show a sample band 6.5 (real eval lands when we wire /api/ai/speaking/eval).
 */
export default function FreeAssessmentResult() {
  const play = useClickSound();
  const [lead, setLead] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("freeAssessmentLead");
      if (raw) setLead(JSON.parse(raw));
    } catch (e) { /* ignore */ }
  }, []);

  const firstName = (lead?.full_name || "Candidate").split(" ")[0];
  const targetExam = lead?.exam || "IELTS";

  // Simulated predicted scores
  const result = {
    overall: 6.5,
    criteria: {
      "Fluency & Coherence": 6.5,
      "Lexical Resource": 6.0,
      "Grammar Range": 6.0,
      "Pronunciation": 7.0,
    },
    nextTarget: 7.5,
    strengths: ["Clear pronunciation with natural rhythm.", "Good use of linking words ('however', 'on the other hand')."],
    improvements: ["Expand lexical range — try 1-2 idiomatic phrases per answer.", "Avoid filler 'like' and 'um' — practise pausing instead.", "Mix complex and simple sentences for a band-7+ grammar range."],
  };

  return (
    <div className="min-h-screen relative pb-20" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="fa-result-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>
      <AIPropsBackground />

      <header className="relative max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <AuthBrand />
      </header>

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-4">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {["Your details", "Speaking mock", "Predicted band"].map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center mono text-[10px] font-bold"
                style={{
                  background: T.primary,
                  color: T.textOnPrimary,
                  border: `1.5px solid ${T.primary}`,
                  boxShadow: i === 2 ? `0 0 12px ${T.primaryGlow}` : "none",
                }}>
                {i + 1}
              </div>
              <div className="mono text-[10px] tracking-[0.25em]" style={{ color: T.text }}>{s.toUpperCase()}</div>
              {i < 2 && <div className="w-8 h-px mx-1" style={{ background: T.primary }} />}
            </div>
          ))}
        </div>

        <div className="text-center mb-8">
          <div className="mono text-[10px] tracking-[0.3em] mb-3 inline-flex items-center gap-1" style={{ color: T.primary }}>
            <Sparkles className="w-3 h-3" /> // YOUR FREE PREDICTION · {targetExam}
          </div>
          <h1 className="display text-4xl md:text-5xl font-bold leading-tight">
            Well done, <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>{firstName}</span>.<br />
            Here&apos;s your predicted band.
          </h1>
        </div>

        {/* Band display */}
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-5">
          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-8" style={{ background: `linear-gradient(180deg, ${T.cardBgWarm}, ${T.cardBg})`, border: `1px solid ${T.borderSoft}` }}>
              <div className="mono text-[10px] tracking-[0.3em]" style={{ color: T.textMuted }}>// PREDICTED OVERALL BAND</div>
              <div className="display font-bold leading-none mt-2" style={{ fontSize: "120px", color: T.primary, textShadow: `0 0 28px ${T.primaryGlow}` }}>
                {result.overall.toFixed(1)}
              </div>
              <div className="text-sm mt-2" style={{ color: T.textMuted }}>
                Estimated from your 3 Speaking responses · accuracy ±0.5
              </div>
              <div className="mt-5 p-4 rounded-sm" style={{ background: T.cardBgWarm, border: `1px solid ${T.borderSoft}` }}>
                <div className="flex items-center gap-2 mono text-[10px] tracking-[0.25em]" style={{ color: T.primary }}>
                  <Trophy className="w-3.5 h-3.5" /> NEXT TARGET
                </div>
                <div className="display text-3xl font-bold mt-1">Band {result.nextTarget.toFixed(1)}</div>
                <div className="text-sm mt-1" style={{ color: T.textMuted }}>+{(result.nextTarget - result.overall).toFixed(1)} bands to your goal · reachable in 6-8 weeks of focused prep.</div>
              </div>
            </div>
          </HudFrame>

          <div className="space-y-5">
            <HudFrame color={T.borderSoft} className="p-1">
              <div className="rounded-sm p-5" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
                <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.textMuted }}>// CRITERIA BREAKDOWN</div>
                <div className="space-y-3">
                  {Object.entries(result.criteria).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: T.text }}>{k}</span>
                        <span className="display font-bold" style={{ color: T.primary }}>{v.toFixed(1)}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-sm" style={{ background: T.borderSoft }}>
                        <div className="h-full rounded-sm" style={{
                          width: `${(v / 9) * 100}%`, background: T.primary,
                          boxShadow: `0 0 8px ${T.primaryGlow}`,
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </HudFrame>
          </div>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div className="rounded-sm p-5" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
            <div className="mono text-[10px] tracking-[0.3em] mb-2" style={{ color: T.success }}>// STRENGTHS</div>
            <ul className="space-y-2">
              {result.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: T.success }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-sm p-5" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
            <div className="mono text-[10px] tracking-[0.3em] mb-2" style={{ color: T.primary }}>// HOW TO IMPROVE</div>
            <ul className="space-y-2">
              {result.improvements.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <ChevronRight className="w-4 h-4 mt-0.5 shrink-0" style={{ color: T.primary }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Upsell — two plans */}
        <div className="mt-10">
          <div className="text-center mb-6">
            <div className="mono text-[10px] tracking-[0.3em] mb-2" style={{ color: T.primary }}>// READY TO REACH BAND {result.nextTarget.toFixed(1)}?</div>
            <h2 className="display text-3xl md:text-4xl font-bold">Pick how you want to train.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <HudFrame color={T.primary} className="p-1.5">
              <div className="rounded-sm p-6 h-full flex flex-col" style={{ background: `linear-gradient(180deg, ${T.cardBgWarm} 0%, ${T.cardBg} 80%)`, border: `1px solid ${T.primary}`, boxShadow: `0 30px 60px -30px ${T.primaryGlow}` }}>
                <Bot className="w-9 h-9" style={{ color: T.primary }} />
                <div className="display text-2xl font-bold mt-3">Self Prep AI</div>
                <div className="text-sm mt-1" style={{ color: T.textMuted }}>Unlimited AI examiners across all 4 skills.</div>
                <div className="display font-bold text-4xl mt-4" style={{ color: T.primary, textShadow: `0 0 18px ${T.primaryGlow}` }}>$14.99<span className="text-base font-normal" style={{ color: T.textMuted }}> /month</span></div>
                <div className="mono text-[10px] tracking-[0.25em] mt-1" style={{ color: T.textMuted }}>CANCEL ANYTIME</div>
                <PrimaryBtn className="w-full mt-5 h-12" data-testid="fa-upsell-self">Subscribe to Self Prep <ArrowRight className="w-4 h-4" /></PrimaryBtn>
              </div>
            </HudFrame>

            <HudFrame color={T.secondary} className="p-1">
              <div className="rounded-sm p-6 h-full flex flex-col" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
                <GraduationCap className="w-9 h-9" style={{ color: T.secondary }} />
                <div className="display text-2xl font-bold mt-3">Hybrid Live + AI</div>
                <div className="text-sm mt-1" style={{ color: T.textMuted }}>Live online classes + everything in Self Prep.</div>
                <div className="display font-bold text-4xl mt-4" style={{ color: T.secondary }}>$54<span className="text-base font-normal" style={{ color: T.textMuted }}> /complete course</span></div>
                <div className="mono text-[10px] tracking-[0.25em] mt-1" style={{ color: T.textMuted }}>8-WEEK COURSE · MONEY-BACK GUARANTEE</div>
                <button onClick={() => play("primary")} data-testid="fa-upsell-hybrid"
                  className="w-full mt-5 h-12 rounded-sm font-bold uppercase tracking-[0.2em] text-sm inline-flex items-center justify-center gap-2 transition-all"
                  style={{ background: "transparent", color: T.secondary, border: `2px solid ${T.secondary}`, fontFamily: "'Rajdhani', sans-serif" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.secondary; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.secondary; }}>
                  Enrol in Hybrid <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </HudFrame>
          </div>
          <div className="text-center mt-5">
            <Link to="/pricing" onClick={() => play("hover")} className="mono text-[11px] tracking-[0.3em] hover:underline" style={{ color: T.textMuted }}>
              <Lock className="w-3 h-3 inline mr-1" /> SEE FULL PRICING & FAQ →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
